import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Button,
  Spin,
  Col,
  Modal,
  Input,
  Row,
} from 'antd';
import { onError } from 'utils/handlers';
import Select from 'components/Select';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import ModalDelete from 'components/ModalDelete';
import {
  deleteColindancia,
  getColindancias,
  patchColindancia,
  postColindancia,
} from 'api/catastro/colindancias';
import { useSelector } from 'react-redux';
import NumericInput from 'components/NumericInput';
import { getOrientacionesDePredios } from 'api/catastro';
import { getPredio, patchPredio, dateKeysPredio } from 'api/catastro/predio';
import { SaveOutlined } from '@ant-design/icons';
import removeIndexes from 'views/Tramites/GPM/removeIndexes';
import formatValues from 'utils/formatValues';

export const permissionConfig = {
  title: 'Medidas y linderos',
  model: 'colindanciadepredios',
  catalogs: ['tipodeorientacionpredio'],
};

export const aplicarAfectacionesColindancias = async ({
  colindancias_de_predios_a_eliminar = [],
  colindancias_de_predios_a_agregar = [],
  colindancias_de_predios_a_modificar = [],
  colindancias = [],
}) => {
  if (!colindancias.length) return;

  await Promise.all(colindancias_de_predios_a_eliminar.map((e) => deleteColindancia(e)));
  await Promise.all(colindancias_de_predios_a_modificar.map((p) => patchColindancia(p.id, p)));
  await Promise.all(colindancias_de_predios_a_agregar.map((p) => postColindancia(p.id, p)));
};

const ColindanciasDePredios = ({
  predio = {},
  mutable,
  permission,
  onUpdatePredio,
  fetchCallback,
  noAfectarPadron,
  callback,
  changePredioPermission,
}) => {
  const [form] = Form.useForm();
  const formInstance = fetchCallback ? Form.useFormInstance() : Form.useForm()[0];
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const orientacionesDePredios = useSelector(({ catalogs }) => catalogs.orientacionesDePredios);

  const fetchData = async () => {
    if (!predio) return;
    setLoading(true);
    if (noAfectarPadron) {
      const _predio = await getPredio(predio.id, false);
      const json = _predio.json_predio ? _predio.json_predio : removeIndexes(_predio);
      json.colindancias_de_predios_a_eliminar = json.colindancias_de_predios_a_eliminar || [];
      json.colindancias_de_predios_a_agregar = json.colindancias_de_predios_a_agregar || [];
      json.colindancias_de_predios_a_modificar = json.colindancias_de_predios_a_modificar || [];
      json.colindancias_de_predios = json.colindancias_de_predios
        || await getColindancias({ predio: predio.id });
      const json_predio = JSON.stringify(formatValues(json, { clean: false }, dateKeysPredio));
      if (mutable) {
        _predio.json_predio = _predio.json_predio_raw !== json_predio
          ? (await patchPredio(predio.id, { json_predio }, false))?.json_predio
          : json;
      }
      setData(json.colindancias_de_predios);
      if (fetchCallback) fetchCallback(json.colindancias_de_predios, _predio.json_predio);
      setLoading(false);
      return;
    }
    const _data = await getColindancias({ predio: predio.id });
    setData(_data);
    if (fetchCallback) fetchCallback(_data);
    setLoading(false);
  };

  useEffect(() => {
    setTimeout(() => formInstance.setFieldsValue({ colindancias: predio.colindancias }));
  }, [predio.colindancias]);

  useEffect(() => {
    let mounted = true;
    const _fetchData = async () => {
      setLoading(true);
      await getOrientacionesDePredios();
      await fetchData();
      if (mounted) {
        setLoading(false);
      }
    };
    _fetchData();
    return () => { mounted = false; };
  }, [predio.id]);

  const onCancel = () => {
    form.resetFields();
    setVisible(false);
    setSelectedRowKeys([]);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      values.predio = predio.id;
      const [key] = selectedRowKeys;
      if (noAfectarPadron) {
        let json_predio = { ...predio };
        if (key) {
          const idx = json_predio.colindancias_de_predios
            .findIndex((e) => e.id === selectedRowKeys[0]);
          json_predio.colindancias_de_predios[idx] = {
            ...json_predio.colindancias_de_predios[idx],
            ...values,
          };
        } else {
          values.id = `temp_id_${(new Date()).toISOString()}`;
          json_predio.colindancias_de_predios.push(values);
        }
        if (typeof key === 'number'
          && !json_predio.colindancias_de_predios_a_modificar.includes(key)) {
          json_predio.colindancias_de_predios_a_modificar.push(key);
        } else if (!key) {
          json_predio.colindancias_de_predios_a_agregar.push(values.id);
        }
        json_predio = JSON.stringify(formatValues(json_predio, { clean: false }, dateKeysPredio));
        const saved = await patchPredio(predio.id, { json_predio }, false);
        if (!saved) {
          setLoading(false);
          return;
        }
        if (callback) await callback(`${key ? 'Agregado' : 'Modificado'} correctamente`);
        await fetchData();
        onCancel();
        return;
      }

      const saved = await (key ? patchColindancia(key, values) : postColindancia(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const onClickEdit = () => {
    setVisible(true);
  };

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    let deleted = false;
    if (noAfectarPadron) {
      let json_predio = { ...predio };
      json_predio.colindancias_de_predios = json_predio.colindancias_de_predios
        .filter((e) => e.id !== selectedRowKeys[0]);
      if (typeof key === 'number') {
        json_predio.colindancias_de_predios_a_eliminar.push(key);
        if (json_predio.colindancias_de_predios_a_modificar.includes(key)) {
          json_predio.colindancias_de_predios_a_modificar = json_predio
            .colindancias_de_predios_a_modificar.filter((e) => e !== key);
        }
      } else {
        json_predio.colindancias_de_predios_a_agregar = json_predio
          .colindancias_de_predios_a_agregar.filter((e) => e !== key);
      }
      json_predio = JSON.stringify(formatValues(json_predio, { clean: false }, dateKeysPredio));
      deleted = await patchPredio(predio.id, { json_predio }, false);
    } else {
      deleted = await deleteColindancia(key);
    }
    if (deleted) {
      if (callback) await callback('Eliminado correctamente');
      await fetchData();
      setVisibleAlert(false);
      onCancel();
    }
    setLoading(false);
  };

  const updatePredio = async () => {
    setLoading(true);
    let values = formInstance.getFieldsValue(['colindancias']);
    if (noAfectarPadron) {
      let json_predio = { ...predio };
      json_predio.colindancias = values.colindancias;
      json_predio = JSON.stringify(formatValues(json_predio, { clean: false }, dateKeysPredio));
      values = { json_predio };
    }
    const saved = await patchPredio(predio.id, values);
    if (saved) {
      onUpdatePredio(noAfectarPadron
        ? { json_predio: saved.json_predio } : { colindancias: saved.colindancias });
    }
    setLoading(false);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Descripción',
      key: 'colindancia',
      dataIndex: 'colindancia',
      width: 300,
    },
    {
      titleText: 'Orientación',
      key: 'orientacion',
      dataIndex: 'orientacion',
      width: 200,
      render: (val) => orientacionesDePredios.find((e) => e.id === val)?.descripcion,
    },
    {
      titleText: 'Distancia',
      key: 'distancia',
      dataIndex: 'distancia',
      width: 200,
      render: (val) => (val ? parseFloat(val).toFixed(8) : null),
    },
  ];

  const rules = { required: [{ required: true }] };
  const WrapperFormColindancias = fetchCallback ? React.Fragment : Form;
  const wrapperFormColindanciasProps = fetchCallback ? {} : {
    form: formInstance,
    layout: 'vertical',
  };

  return (
    <>
      <Spin tip="Cargando..." spinning={loading}>
        <br />
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickDelete,
            onClickEdit,
          }}
          noControls={!mutable}
          allowSort={false}
          allowSearch={false}
          rowKey="id"
          permission={permission}
        />
        <Modal
          open={visible}
          closable={false}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Colindancia De Predio`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              permission={permission}
            />
          )}
          keyboard={!loading}
          maskClosable={!loading}
          onCancel={onCancel}
          forceRender
        >
          <Col span={24}>
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
            >
              <Col span={24}>
                <Form.Item
                  label="Orientación"
                  name="orientacion"
                  rules={rules.required}
                >
                  <Select
                    dataSource={orientacionesDePredios}
                    disabled={!mutable}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Descripción"
                  name="colindancia"
                  rules={rules.required}
                >
                  <Input disabled={!mutable} allowClear />
                </Form.Item>
              </Col>
              <Col span={24}>
                <NumericInput
                  label="Distancia"
                  name="distancia"
                  disabled={!mutable}
                  precision={8}
                  decimal
                />
              </Col>
              <Form.Item name="id" hidden>
                <Input />
              </Form.Item>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>
          </Col>
        </Modal>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <WrapperFormColindancias {...wrapperFormColindanciasProps}>
          <Row gutter={10}>
            <Col xs={24} sm={24} md={16} lg={20}>
              <Form.Item label="Colindancias" name="colindancias">
                <Input.TextArea
                  autoSize={{ minRows: 5 }}
                  disabled={!mutable || !changePredioPermission}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={4}>
              <Form.Item label=" ">
                <Button
                  disabled={!mutable || !changePredioPermission}
                  onClick={updatePredio}
                >
                  Guardar
                  <SaveOutlined />
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </WrapperFormColindancias>
      </Spin>
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => setVisibleAlert(false)}
        visible={visibleAlert}
        content="la Colindancia de predio seleccionado"
      />
    </>
  );
};

ColindanciasDePredios.propTypes = {
  predio: PropTypes.shape(),
  mutable: PropTypes.bool,
  onUpdatePredio: PropTypes.func.isRequired,
  fetchCallback: PropTypes.func,
  permission: PropTypes.shape().isRequired,
  noAfectarPadron: PropTypes.bool,
  callback: PropTypes.func,
  changePredioPermission: PropTypes.bool,
};

ColindanciasDePredios.defaultProps = {
  predio: null,
  mutable: true,
  fetchCallback: null,
  noAfectarPadron: false,
  callback: null,
  changePredioPermission: false,
};

export default ColindanciasDePredios;
